import React, { useState, useCallback, useRef, useEffect } from "react";
import { useAppSelector, useAppDispatch } from "../../../../../redux/store";
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
  Pagination,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIconMui,
} from "@mui/icons-material";
import {
  searchDropbox,
  clearSearchResults,
} from "../../../../../redux/slices/dropboxFoldersSlice";
import { FileIcon } from "../shared/FileIcon";

const RESULTS_PER_PAGE = 50;

const DeepSearch = ({ onResultClick, projectId }: any) => {
  const dispatch = useAppDispatch();
  const {
    searchResults,
    searchLoading,
    searchError,
    searchTotalCount,
    searchPage,
  } = useAppSelector((state) => state.dropboxFolders) as any;

  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState(""); // "", "file", "folder"
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef<any>(null);
  const abortRef = useRef<any>(null);

  const executeSearch = useCallback(
    (searchQuery: any, type: any, page = 1) => {
      if (!searchQuery.trim()) return;

      // Abort previous request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      abortRef.current = new AbortController();

      setHasSearched(true);
      dispatch(
        searchDropbox({
          query: searchQuery.trim(),
          type,
          page,
          limit: RESULTS_PER_PAGE,
          projectId: projectId || "",
        }),
      );
    },
    [dispatch, projectId],
  );

  const handleSearch = () => {
    executeSearch(query, typeFilter, 1);
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleQueryChange = (e: any) => {
    const value = e.target.value;
    setQuery(value);

    // Debounced search after 500ms of no typing
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.trim().length >= 3) {
      debounceRef.current = setTimeout(() => {
        executeSearch(value, typeFilter, 1);
      }, 500);
    } else if (value.trim().length === 0) {
      dispatch(clearSearchResults());
      setHasSearched(false);
    }
  };

  const handleClear = () => {
    setQuery("");
    setHasSearched(false);
    dispatch(clearSearchResults());
    if (debounceRef.current) clearTimeout(debounceRef.current);
  };

  const handleTypeFilterChange = (_event: any, newType: any) => {
    const type = newType || "";
    setTypeFilter(type);
    if (query.trim().length >= 3) {
      executeSearch(query, type, 1);
    }
  };

  const handlePageChange = (_event: any, newPage: any) => {
    executeSearch(query, typeFilter, newPage);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
      dispatch(clearSearchResults());
    };
  }, [dispatch]);

  const totalPages = Math.ceil(searchTotalCount / RESULTS_PER_PAGE);

  const formatFileSize = (bytes: any) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getParentDisplay = (pathDisplay: any) => {
    if (!pathDisplay) return "";
    const parts = pathDisplay.split("/").filter(Boolean);
    if (parts.length <= 1) return "/";
    return "/" + parts.slice(0, -1).join("/");
  };

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 0 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
        Deep Search
      </Typography>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          fullWidth
          sx={{
            "& .MuiInputBase-input": {
              padding: "4.5px 14px",
            },
          }}
          placeholder="Search all files and folders..."
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: query && (
              <InputAdornment position="end">
                <Button
                  size="small"
                  onClick={handleClear}
                  sx={{ minWidth: "auto", p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </Button>
              </InputAdornment>
            ),
            sx: { borderRadius: 0 },
          }}
          size="small"
        />

        <ToggleButtonGroup
          value={typeFilter}
          exclusive
          onChange={handleTypeFilterChange}
          size="small"
          sx={{ height: "32px" }}
        >
          <ToggleButton value="" sx={{ px: 1.5 }}>
            All
          </ToggleButton>
          <ToggleButton value="folder" sx={{ px: 1.5 }}>
            <FolderOpenIcon fontSize="small" sx={{ mr: 0.5 }} />
            Folders
          </ToggleButton>
          <ToggleButton value="file" sx={{ px: 1.5 }}>
            <FileIconMui fontSize="small" sx={{ mr: 0.5 }} />
            Files
          </ToggleButton>
        </ToggleButtonGroup>

        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={searchLoading || query.trim().length < 3}
          size="small"
          sx={{
            borderRadius: 0,
            minWidth: "auto",
            whiteSpace: "nowrap",
            height: "32px",
          }}
        >
          Search
        </Button>
      </Box>

      {/* Results */}
      <Collapse in={hasSearched}>
        <Box sx={{ mt: 2 }}>
          {searchLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {searchError && (
            <Typography variant="body2" color="error" sx={{ py: 1 }}>
              Search failed: {searchError}
            </Typography>
          )}

          {!searchLoading && !searchError && searchResults.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No results found for &quot;{query}&quot;
            </Typography>
          )}

          {!searchLoading && searchResults.length > 0 && (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {searchTotalCount} result{searchTotalCount !== 1 ? "s" : ""}{" "}
                  found
                </Typography>
              </Box>

              <List dense disablePadding>
                {searchResults.map((item: any, index: any) => (
                  <React.Fragment key={item._id || index}>
                    <ListItem disablePadding>
                      <ListItemButton
                        sx={{ borderRadius: 0, py: 0.5 }}
                        onClick={() => onResultClick?.(item)}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <FileIcon fileName={item.name} type={item.type} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          secondary={
                            <Box
                              component="span"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                component="span"
                              >
                                {getParentDisplay(
                                  item.path_display || item.path,
                                )}
                              </Typography>
                              {item.size > 0 && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  component="span"
                                >
                                  {formatFileSize(item.size)}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Chip
                          label={item.type === "file" ? "File" : "Folder"}
                          size="small"
                          color={item.type === "file" ? "info" : "primary"}
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {index < searchResults.length - 1 && (
                      <Divider sx={{ mx: 1 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>

              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={searchPage}
                    onChange={handlePageChange}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default DeepSearch;
